import React, { useCallback, useRef } from "react";

import CardFileGrid, { GridFileResponse } from "./cardFileGrid";
import { PaginationCustomRequest, PaginationRequest } from "@/main/models/common/paginationRequest";
import { PaginationResponse } from "@/main/models/common/paginationResponse";
import KOGPaginationGridVirtualized from "@/main/components/shared/paginationGrid/paginationGridVirtualized";

interface IFilesTabGridProps<Request extends PaginationRequest, Response extends { guid: string }> {
	readonly dataGrid: Response[];
	readonly totalRecords: number;
	readonly formFilters: Request | null;
	readonly isLoading: boolean;
	readonly setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	readonly setTotalRecords: React.Dispatch<React.SetStateAction<number>>;
	readonly setDataGrid: React.Dispatch<React.SetStateAction<Response[]>>;
	readonly fetchData: (request: PaginationRequest | PaginationCustomRequest) => Promise<PaginationResponse<Response>>;
	readonly openFileInModalCarousel: (file: Response) => void;
	readonly mapToGridFile: (item: Response) => GridFileResponse;
	readonly canDownload?: boolean;
	readonly handleDownload?: (url: string, file?: Response) => void;
}

function FileGrid<Request extends PaginationRequest, Response extends { guid: string }>({
	dataGrid,
	formFilters,
	totalRecords,
	isLoading,
	setIsLoading,
	setTotalRecords,
	setDataGrid,
	fetchData,
	openFileInModalCarousel,
	mapToGridFile,
	canDownload,
	handleDownload,
}: IFilesTabGridProps<Request, Response>) {
	const openFileRef = useRef(openFileInModalCarousel);
	openFileRef.current = openFileInModalCarousel;

	const mapToGridFileRef = useRef(mapToGridFile);
	mapToGridFileRef.current = mapToGridFile;

	const renderItem = useCallback((item: Response) => {
		const file = mapToGridFileRef.current(item);
		return (
			<CardFileGrid
				key={file.guid}
				file={file}
				openFileInModalCarousel={() => openFileRef.current(item)}
				canDownload={canDownload}
				handleDownload={(url: string) => {
					if (handleDownload) {
						handleDownload(url, item);
					}
				}}
			/>
		);
	}, []);
	return (
		<KOGPaginationGridVirtualized
			showTitleResults
			maxHeightScrollInternal="calc(100vh - 280px)"
			showEndMessage={false}
			dataGrid={dataGrid}
			totalRecords={totalRecords}
			isLoading={isLoading}
			setIsLoading={setIsLoading}
			setTotalRecords={setTotalRecords}
			setDataGrid={setDataGrid}
			fetchMore={fetchData}
			formFilters={formFilters}
			itemWidth={200}
			itemHeight={190}
			gap={16}
			renderItem={renderItem}
		/>
	);
}

export default FileGrid;
